import { Icon, Tooltip } from '@dixa/lib-shared-habla';
import * as Popover from '@radix-ui/react-popover';
import classNames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import { MouseEvent, RefObject, useCallback, useEffect, useState } from 'react';

import { appActions } from 'app/app.actions';
import { rewindConfig } from 'components/rewind';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { useRewindContext } from '../../rewindModal.context';
import { RewindSlideId } from '../../rewindModal.types';
import { getRewindSummary } from '../../utils';

import { LinkedInLogo } from './components/rewindSocialLogos';
import styles from './rewindControls.module.scss';

type RewindControlsProps = {
  isVisible: boolean;
  isMuted: boolean;
  isPaused: boolean;
  isFirstSlide: boolean;
  isLastSlide: boolean;
  onToggleMute: () => void;
  onTogglePause: () => void;
  onPrevious: () => void;
  onNext: () => void;
  slideContainerRef: RefObject<HTMLDivElement | null>;
  currentSlideId: RewindSlideId;
  onCopyImage: (
    element: HTMLElement,
    slideId: RewindSlideId,
  ) => Promise<boolean>;
  onSaveImage: (
    element: HTMLElement,
    slideId: RewindSlideId,
    filename?: string,
  ) => Promise<void>;
  isCopying: boolean;
  isSaving: boolean;
};

export function RewindControls({
  isVisible,
  isMuted,
  isPaused,
  isFirstSlide,
  isLastSlide,
  onToggleMute,
  onTogglePause,
  onPrevious,
  onNext,
  slideContainerRef,
  currentSlideId,
  onCopyImage,
  onSaveImage,
  isCopying,
  isSaving,
}: RewindControlsProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { data } = useRewindContext();
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [copyTextSuccess, setCopyTextSuccess] = useState(false);

  // Reset success states after a delay
  useEffect(() => {
    if (copySuccess) {
      const timer = setTimeout(() => setCopySuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copySuccess]);

  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => setSaveSuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);

  useEffect(() => {
    if (copyTextSuccess) {
      const timer = setTimeout(() => setCopyTextSuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copyTextSuccess]);

  const handleButtonClick =
    (handler: () => void) => (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      handler();
    };

  const stopPropagation = (e: MouseEvent): void => {
    e.stopPropagation();
  };

  const handleCopyImage = useCallback(async () => {
    const element = slideContainerRef?.current;
    if (!element || isCopying) return;

    const success = await onCopyImage(element, currentSlideId);

    if (success) {
      setCopySuccess(true);

      ampli.rewindSlideImageCopied({
        year: rewindConfig.year,
        slide: currentSlideId,
      });
    }
  }, [slideContainerRef, currentSlideId, onCopyImage, isCopying]);

  const handleSaveImage = useCallback(async () => {
    const element = slideContainerRef?.current;
    if (!element || isSaving) return;

    const filename = `dixa-rewind-${rewindConfig.year}-${currentSlideId}.png`;
    await onSaveImage(element, currentSlideId, filename);
    setSaveSuccess(true);

    ampli.rewindSlideImageSaved({
      year: rewindConfig.year,
      slide: currentSlideId,
    });
  }, [slideContainerRef, currentSlideId, onSaveImage, isSaving]);

  const handleSendFeedback = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      ampli.rewindFeedbackClicked({
        year: rewindConfig.year,
        slide: currentSlideId,
      });

      dispatch(
        appActions.showModal({
          modal: {
            type: 'app:feedbackForm',
            arg: {
              title: `Dixa Rewind ${rewindConfig.year} Feedback`,
              team: 'insights',
              placeholder:
                'Tell us what you think about Dixa Rewind! Any suggestions or issues?',
              feature: 'rewind',
              extra: {
                rewindData: JSON.stringify(data),
              },
            },
          },
        }),
      );
    },
    [currentSlideId, data, dispatch],
  );

  const handleCopyText = useCallback(async () => {
    if (!data) return;

    try {
      const summary = getRewindSummary(data);
      await navigator.clipboard.writeText(summary);

      setCopyTextSuccess(true);

      ampli.rewindSlideTextCopied({
        year: rewindConfig.year,
        slide: currentSlideId,
      });
    } catch (error) {
      console.error('Failed to copy text:', error);
    }
  }, [currentSlideId, data]);

  const handleShareLinkedIn = useCallback(() => {
    if (!data) return;

    const summary = getRewindSummary(data);

    ampli.rewindShareToLinkedInClicked({
      year: rewindConfig.year,
      slide: currentSlideId,
    });

    window.open(
      `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(summary)}`,
      '_blank',
    );

    setIsShareOpen(false);
  }, [currentSlideId, data]);

  return (
    <div>
      {/* Left controls - Navigation & Playback */}
      <div
        className={classNames(styles.controls, styles.leftControls, {
          [styles.visible]: isVisible,
        })}
        onMouseDown={stopPropagation}
        onMouseUp={stopPropagation}
        data-capture-ignore
      >
        {/* Previous */}
        <Tooltip content="Previous slide">
          <button
            className={classNames(styles.controlButton, {
              [styles.disabled]: isFirstSlide,
            })}
            onClick={handleButtonClick(onPrevious)}
            aria-label="Previous slide"
            disabled={isFirstSlide}
          >
            <Icon name="arrow-left" size={16} />
          </button>
        </Tooltip>

        {/* Play/Pause */}
        <Tooltip content={isPaused ? 'Play' : 'Pause'}>
          <button
            className={styles.controlButton}
            onClick={handleButtonClick(onTogglePause)}
            aria-label={isPaused ? 'Play' : 'Pause'}
          >
            <Icon name={isPaused ? 'play' : 'pause'} size={16} />
          </button>
        </Tooltip>

        {/* Next */}
        <Tooltip content="Next slide">
          <button
            className={classNames(styles.controlButton, {
              [styles.disabled]: isLastSlide,
            })}
            onClick={handleButtonClick(onNext)}
            aria-label="Next slide"
            disabled={isLastSlide}
          >
            <Icon name="arrow-right" size={16} />
          </button>
        </Tooltip>

        {/* Mute/Unmute */}
        <Tooltip content={isMuted ? 'Unmute' : 'Mute'}>
          <button
            className={styles.controlButton}
            onClick={handleButtonClick(onToggleMute)}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            <Icon name={isMuted ? 'volume-mute' : 'volume-up'} size={16} />
          </button>
        </Tooltip>
      </div>

      {/* Right controls - Feedback & Share */}
      <div
        className={classNames(styles.controls, styles.rightControls, {
          [styles.visible]: isVisible,
        })}
        onMouseDown={stopPropagation}
        onMouseUp={stopPropagation}
        data-capture-ignore
      >
        {/* Share */}
        <Popover.Root open={isShareOpen} onOpenChange={setIsShareOpen}>
          <Tooltip content="Share">
            <Popover.Trigger asChild>
              <button
                className={styles.controlButton}
                onClick={(e) => e.stopPropagation()}
              >
                <Icon name="flow" size={16} />
              </button>
            </Popover.Trigger>
          </Tooltip>

          <Popover.Portal>
            <Popover.Content
              className={styles.sharePopover}
              side="top"
              align="end"
              sideOffset={8}
              onClick={(e) => e.stopPropagation()}
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <div className={styles.shareMenu}>
                {/* Copy & Save options - only shown if image generation is supported */}
                {rewindConfig.canGenerateImages && (
                  <>
                    <Tooltip content="Copy this slide as an image" side="left">
                      <button
                        className={classNames(styles.shareMenuItem, {
                          [styles.disabled]: isCopying,
                          [styles.success]: copySuccess,
                        })}
                        onClick={handleCopyImage}
                        disabled={isCopying || copySuccess}
                      >
                        <Icon
                          name={copySuccess ? 'check-circle' : 'copy'}
                          size={16}
                        />
                        <span>
                          {isCopying
                            ? 'Copying...'
                            : copySuccess
                              ? 'Copied!'
                              : 'Copy image'}
                        </span>
                      </button>
                    </Tooltip>

                    <Tooltip
                      content="Download this slide as an image"
                      side="left"
                    >
                      <button
                        className={classNames(styles.shareMenuItem, {
                          [styles.disabled]: isSaving,
                          [styles.success]: saveSuccess,
                        })}
                        onClick={handleSaveImage}
                        disabled={isSaving || saveSuccess}
                      >
                        <Icon
                          name={saveSuccess ? 'check-circle' : 'download'}
                          size={16}
                        />
                        <span>
                          {isSaving
                            ? 'Saving...'
                            : saveSuccess
                              ? 'Saved!'
                              : 'Save image'}
                        </span>
                      </button>
                    </Tooltip>

                    <div className={styles.shareMenuDivider} />
                  </>
                )}

                <Tooltip content="Copy your stats as plain text" side="left">
                  <button
                    className={classNames(styles.shareMenuItem, {
                      [styles.success]: copyTextSuccess,
                    })}
                    onClick={handleCopyText}
                    disabled={copyTextSuccess}
                  >
                    <Icon
                      name={copyTextSuccess ? 'check-circle' : 'file-text'}
                      size={16}
                    />
                    <span>
                      {copyTextSuccess ? 'Copied!' : 'Copy your stats'}
                    </span>
                  </button>
                </Tooltip>

                <div className={styles.shareMenuDivider} />

                {/* Social sharing options */}
                <Tooltip content="Share your stats on LinkedIn" side="left">
                  <button
                    className={styles.shareMenuItem}
                    onClick={handleShareLinkedIn}
                  >
                    <LinkedInLogo size={16} />
                    <span>Share on LinkedIn</span>
                  </button>
                </Tooltip>
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>

        {/* Send Feedback */}
        <Tooltip content="Send feedback">
          <button
            className={styles.controlButton}
            onClick={handleSendFeedback}
            aria-label="Send feedback"
          >
            <Icon name="paper-plane" size={16} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
